import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { subscribeToBlueprint } from '../services/firestoreService';
import OrganizationChart from '../components/OrganizationChart';
import chroma from 'chroma-js';
import { useTheme } from '@mui/material/styles';
import * as MuiIcons from '@mui/icons-material';
import ModulePreview from '../components/ModulePreview';
import SplashManager from '../animations/launch/SplashManager';
import SignatureCanvas from 'react-signature-canvas';
import {
  createPaymentIntent,
  processPayment,
  stripePromiseInstance,
} from '../services/stripeService';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

// Composant de formulaire de paiement
const PaymentForm = ({
  clientSecret,
  onSuccess,
  onError,
  blueprintData,
  t,
  blueprintId,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isStripeReady, setIsStripeReady] = useState(false);

  useEffect(() => {
    if (stripe && elements) {
      setIsStripeReady(true);
    }
  }, [stripe, elements]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      console.error('Stripe or Elements not loaded');
      setError(t('payment.stripe_not_initialized'));
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?blueprintId=${blueprintId}`,
        },
      });

      if (submitError) {
        console.error('Payment error:', submitError);
        let errorMessage = t('payment.unknown_error');

        switch (submitError.type) {
          case 'card_error':
          case 'validation_error':
            errorMessage = submitError.message;
            break;
          case 'invalid_request_error':
            errorMessage = t('payment.invalid_request');
            break;
          case 'api_error':
            errorMessage = t('payment.api_error');
            break;
          default:
            errorMessage = submitError.message || t('payment.unknown_error');
        }

        setError(errorMessage);
        onError(errorMessage);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      const errorMessage = error.message || t('payment.unknown_error');
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isStripeReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isProcessing || !stripe}
          sx={{
            backgroundColor: blueprintData?.mainColor || 'primary.main',
            '&:hover': {
              backgroundColor: blueprintData?.mainColor || 'primary.main',
              opacity: 0.9,
            },
          }}
        >
          {isProcessing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={24} color="inherit" />
              <Typography variant="body2">{t('payment.processing')}</Typography>
            </Box>
          ) : (
            t('payment.pay')
          )}
        </Button>
      </Box>
    </form>
  );
};

const PublicBlueprintPage = () => {
  const { blueprintId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [blueprintData, setBlueprintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const printRef = React.useRef();
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [selectedModule, setSelectedModule] = useState(null);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(false);
  const signatureRef = useRef();
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);

  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [forceRerender, setForceRerender] = useState(0);

  // Color scales for background effects
  const lightGreyScale = chroma
    .scale([blueprintData?.mainColor || theme.palette.primary.main, '#f2f2f2'])
    .mode('lab')
    .colors(10)
    .map((color) => chroma(color).brighten(1.4));

  const lightGreyScaleSec = chroma
    .scale([blueprintData?.secColor || theme.palette.secondary.main, '#f2f2f2'])
    .mode('lab')
    .colors(10)
    .map((color) => chroma(color).brighten(1.4));

  const darkColor = () => {
    let finalColor = blueprintData?.mainColor || theme.palette.primary.main;
    return chroma(finalColor).darken(0.9).hex();
  };

  useEffect(() => {
    if (!blueprintId) {
      setError('No blueprint ID provided');
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToBlueprint(blueprintId, (data) => {
      if (!data) {
        setError('Blueprint not found');
        setLoading(false);
        return;
      }

      setBlueprintData(data);
      setLoading(false);
      setError(null);
    });

    return () => {
      unsubscribe();
    };
  }, [blueprintId]);

  useEffect(() => {
    if (blueprintData?.mainColor) {
      // Ensure mainColor has a # prefix and is properly formatted
      const color = blueprintData.mainColor.startsWith('#')
        ? blueprintData.mainColor
        : `#${blueprintData.mainColor}`;

      // Set the CSS variable for the animation
      document.documentElement.style.setProperty('--businessColor', color);

      setForceRerender((prev) => prev + 1);
    }

    return () => {
      document.documentElement.style.removeProperty('--businessColor');
    };
  }, [blueprintData]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const blockPrices = blueprintData.structures.reduce((acc, structure) => {
    if (!structure.tabs) return acc;
    return (
      acc +
      structure.tabs.reduce((tabAcc, tab) => {
        if (!tab.blocks) return tabAcc;
        return (
          tabAcc +
          tab.blocks.reduce((blockAcc, block) => {
            return (
              blockAcc + (block.price !== undefined ? Number(block.price) : 0)
            );
          }, 0)
        );
      }, 0)
    );
  }, 0);

  const modulePrices = blueprintData.modules.reduce((acc, module) => {
    return acc + (module.price !== undefined ? Number(module.price) : 0);
  }, 0);

  const totalPrice = blockPrices + modulePrices;

  const getModuleTypeIcon = (listType) => {
    switch (listType) {
      case 'listA':
        return <MuiIcons.ListAlt sx={{ fontSize: 9, marginRight: '4px' }} />;
      case 'listS':
        return <MuiIcons.List sx={{ fontSize: 9, marginRight: '4px' }} />;
      case 'calendar':
        return (
          <MuiIcons.CalendarToday sx={{ fontSize: 9, marginRight: '4px' }} />
        );
      case 'kanban':
        return <MuiIcons.ViewKanban sx={{ fontSize: 9, marginRight: '4px' }} />;
      case 'table':
        return <MuiIcons.TableChart sx={{ fontSize: 9, marginRight: '4px' }} />;
      default:
        return (
          <MuiIcons.DescriptionOutlined
            sx={{ fontSize: 9, marginRight: '4px' }}
          />
        );
    }
  };

  const handleApproveClick = () => {
    setOpenContractDialog(true);
  };

  const handleCloseContract = () => {
    setOpenContractDialog(false);
    setContractAccepted(false);
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleContractSubmit = async () => {
    if (contractAccepted && !isSignatureEmpty) {
      try {
        // Convertir le montant en cents (Stripe utilise les cents)
        const amountInCents = Math.round(totalPrice * 100);

        // Créer l'intention de paiement
        const { clientSecret } = await createPaymentIntent(
          amountInCents,
          blueprintData?.customerId
        );

        setClientSecret(clientSecret);
        setShowPaymentForm(true);
      } catch (error) {
        console.error('Error creating payment intent:', error);
        setPaymentError(
          error.message ||
            'Une erreur est survenue lors de la préparation du paiement'
        );
      }
    }
  };

  const handleSignatureEnd = () => {
    setIsSignatureEmpty(signatureRef.current.isEmpty());
  };

  const handleClearSignature = () => {
    signatureRef.current.clear();
    setIsSignatureEmpty(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true);
    handleCloseContract();
    handleClosePayment();
  };

  const handlePaymentError = (error) => {
    setPaymentError(error);
  };

  const handleClosePayment = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
    setPaymentError(null);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{ height: '100vh', overflow: isMobile ? 'auto' : 'hidden' }}
    >
      <SplashManager
        key={`launch-${forceRerender}`}
        blueprintName={blueprintData?.name}
        mainColor={
          blueprintData?.mainColor
            ? blueprintData.mainColor.startsWith('#')
              ? blueprintData.mainColor
              : `#${blueprintData.mainColor}`
            : undefined
        }
      />
      <Box
        sx={{
          p: 3,
          mt: 5,
          width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 3,
          minHeight: '100%',
          pb: 5,
        }}
      >
        <Box
          sx={{
            width: isMobile ? '100%' : '60%',
            overflowY: 'auto',
            height: isMobile ? 'auto' : '100%',
            paddingRight: isMobile ? 0 : '20px',
          }}
          ref={printRef}
        >
          <Paper elevation={3} sx={{ p: 3, width: '100%' }}>
            <Box sx={{ textAlign: 'left', mb: 4 }}>
              <img
                style={{
                  width: '40px',
                  height: '40px',
                  marginBottom: '6px',
                  borderRadius: '20px',
                }}
                alt="Logo"
                src={`https://storage.googleapis.com/business-logos-node/${blueprintData?.ownerId}.png`}
              />
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                {blueprintData?.name || t('blueprint_recap')}
              </Typography>
              <div>
                <Typography variant="subtitle1" color="text.secondary">
                  {blueprintData?.address}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {blueprintData?.phone}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {blueprintData?.email}
                </Typography>
              </div>
            </Box>
          </Paper>
          {!isMobile && (
            <Paper
              elevation={3}
              sx={{
                mt: 3,
                p: 3,
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                padding: '15px 25px',
                borderRadius: '20px',
                height: isMobile ? 'auto' : '80vh',
                maxHeight: isMobile ? 'none' : '540px',
                minHeight: isMobile ? '400px' : '540px',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  zIndex: -1,
                }}
              >
                <div
                  style={{
                    width: '500px',
                    height: '300px',
                    overflow: 'hidden',
                    backgroundColor: lightGreyScale[0] + '60',
                    borderRadius: '50%',
                    filter: 'blur(120px)',
                    position: 'absolute',
                    top: '220px',
                    left: '-420px',
                  }}
                />
                <div
                  style={{
                    width: '800px',
                    height: '500px',
                    overflow: 'hidden',
                    backgroundColor: lightGreyScaleSec[2] + '50',
                    borderRadius: '50%',
                    filter: 'blur(120px)',
                    position: 'absolute',
                    top: '-120px',
                    right: '-310px',
                  }}
                />
                <div
                  style={{
                    width: '300px',
                    height: '200px',
                    overflow: 'hidden',
                    backgroundColor: isDarkMode ? '#00000040' : '#FFFFFF',
                    rotate: '325deg',
                    borderRadius: '80px',
                    filter: 'blur(120px)',
                    position: 'absolute',
                    bottom: '-190px',
                    left: '-220px',
                  }}
                />
                <div
                  style={{
                    width: '400px',
                    height: '400px',
                    overflow: 'hidden',
                    backgroundColor: lightGreyScale[0] + '60',
                    borderRadius: '50%',
                    filter: 'blur(120px)',
                    position: 'absolute',
                    bottom: '-80px',
                    left: '-80px',
                  }}
                />
              </Box>
              <div
                className="row"
                style={{
                  backgroundColor: '#FFFFFF70',
                  border: '1px solid #FFFFFF',
                  borderRadius: '10px',
                  padding: '12px',
                  height: '100%',
                  paddingLeft: '12px',
                }}
              >
                {!isMobile && (
                  <div
                    className="col-3 py-4 px-4"
                    style={{
                      backgroundColor: blueprintData?.mainColor + 20,
                      borderRadius: '10px',
                    }}
                  >
                    <Button
                      className="middle-content"
                      fullWidth={true}
                      sx={{
                        zIndex: 100,
                        width: '100%',
                        maxWidth: '100% !important',
                        minWidth: '112px !important',
                        height: '24px',
                        background: `linear-gradient(145deg, ${
                          blueprintData?.mainColor || '#FF6F91'
                        }, ${darkColor()})`,
                        borderRadius: '5px',
                        padding: '0.4rem',
                        boxShadow: `0px 4px 10px rgba(0, 0, 0, 0.1), 0px -2px 4px rgba(255, 255, 255, 0.2) inset`,
                        color: '#FFFFFF',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          background: `linear-gradient(145deg,${darkColor()}, ${
                            blueprintData?.mainColor || '#FF6F91'
                          })`,
                          boxShadow: `0px 6px 15px rgba(0, 0, 0, 0.2), 0px -3px 6px rgba(255, 255, 255, 0.3) inset`,
                          transform: 'scale(1.04)',
                          cursor: 'pointer',
                        },
                        '&:active': {
                          transform: 'scale(1)',
                          boxShadow: `0px 3px 8px rgba(0, 0, 0, 0.2), 0px -2px 3px rgba(255, 255, 255, 0.3) inset`,
                        },
                      }}
                    >
                      <MuiIcons.AddOutlined
                        fontSize="medium"
                        htmlColor={'#FFF'}
                        sx={{
                          marginRight: '5px',
                          marginTop: '-1px',
                          fontSize: '11px',
                        }}
                      />
                      <Typography
                        fontWeight={600}
                        fontSize="9px"
                        color={'#FFF'}
                        variant="body2"
                      >
                        {t('new')}
                      </Typography>
                    </Button>
                    <div className="mt-3 px-1">
                      <div className={'d-flex align-left mb-2'}>
                        <div
                          className="hover"
                          style={{ marginLeft: '2px', marginTop: '0px' }}
                        >
                          <Typography
                            fontWeight={600}
                            fontSize="10.5px"
                            color={darkColor()}
                            variant="body2"
                          >
                            {t('OPERATIONS')}
                          </Typography>
                        </div>
                        <div style={{ marginTop: '-3px' }}>
                          <MuiIcons.ArrowDropDown
                            sx={{ color: darkColor(), fontSize: '14px' }}
                          />
                        </div>
                      </div>
                      {blueprintData?.modules
                        ?.filter((m) => m?.section === 'OPERATIONS')
                        ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                        ?.map((module) => {
                          const IconName =
                            module?.icon || 'DescriptionOutlined';
                          const IconComponent =
                            MuiIcons[IconName] || MuiIcons.DescriptionOutlined;
                          const listType = module?.list?.tabs?.[0]?.listType;
                          return (
                            <div
                              className="d-flex middle-content mt-1 mb-2"
                              onClick={() => setSelectedModule(module)}
                              style={{ cursor: 'pointer' }}
                            >
                              <IconComponent
                                sx={{ fontSize: 9, marginRight: '4px' }}
                              />

                              <Typography fontSize={9} fontWeight={500}>
                                {module.name_fr ||
                                  module.name_en ||
                                  module.name}
                              </Typography>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                <div
                  style={{ paddingLeft: '20px' }}
                  className="col-md-9 col-12"
                >
                  <Grid item xs={12} md={6}>
                    <div className="row m-1">
                      <div className="col-2">
                        <div>
                          <Typography fontSize={11} fontWeight={600}>
                            {selectedModule?.name_fr ||
                              selectedModule?.name_en ||
                              selectedModule?.name}
                          </Typography>
                        </div>
                        <div>
                          <Typography
                            fontSize={7}
                            fontWeight={400}
                            className="greyText"
                            sx={{ marginTop: '-2px' }}
                          >
                            {'-'}
                          </Typography>
                        </div>
                      </div>
                      <div className="col-4 d-flex" style={{ gap: 3 }}></div>
                    </div>
                    {selectedModule && (
                      <ModulePreview
                        module={selectedModule}
                        mainColor={blueprintData?.mainColor}
                        theme={theme}
                        structures={blueprintData.structures}
                      />
                    )}
                  </Grid>
                </div>
              </div>
            </Paper>
          )}
          <Paper
            className="d-flex justify-content-between"
            elevation={3}
            sx={{ p: 3, mt: 3, width: '100%' }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h5" fontSize={12} sx={{ fontWeight: 600 }}>
                {t('blueprint.approve_blueprint')}
              </Typography>
              <Typography
                variant="h5"
                fontSize={10}
                sx={{ fontWeight: 500, color: 'text.secondary' }}
              >
                {t('blueprint.approve_blueprint_description')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'left' }}>
              <Button
                variant="contained"
                color="black"
                onClick={handleApproveClick}
              >
                {t('blueprint.approve')}
              </Button>
            </Box>
          </Paper>
        </Box>
        <Box
          sx={{
            width: isMobile ? '100%' : '40%',
            overflowY: 'hidden',
            height: isMobile ? 'auto' : 'calc(100vh - 70px)',
            mt: isMobile ? 3 : 0,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 3,
              width: '100%',
              height: isMobile ? 'auto' : 'calc(100vh - 85px)',
              minHeight: isMobile ? '500px' : 'auto',
            }}
          >
            <Box
              sx={{
                mb: 4,
                p: 1,
                borderRadius: '8px',
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  width: '100%',
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{
                    '& .MuiTabs-flexContainer': {
                      justifyContent: 'flex-end',
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 'medium',
                      minWidth: 'auto',
                      padding: '0 16px',
                      minHeight: 'auto',
                      py: 1,
                      px: 2,
                      borderRadius: '20px',
                      '&.Mui-selected': {
                        backgroundColor:
                          blueprintData?.mainColor || 'primary.main',
                        color: 'white',
                      },
                    },
                  }}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                >
                  <Tab
                    icon={<AccountTreeIcon />}
                    label={t('blueprint.organization')}
                    iconPosition="start"
                  />
                  <Tab
                    icon={<AssessmentIcon />}
                    label={t('blueprint.recapitulative')}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>
              {activeTab === 0 && (
                <>
                  {' '}
                  <Box
                    sx={{
                      textAlign: 'left',
                      mb: 4,
                      height: isMobile ? '500px' : '81vh',
                      overflowY: 'auto',
                    }}
                  >
                    <OrganizationChart
                      structures={blueprintData.structures}
                      mainColor={blueprintData?.mainColor}
                      secColor={blueprintData?.secColor}
                    />
                  </Box>
                </>
              )}
              {activeTab === 1 && (
                <>
                  <Box
                    sx={{
                      textAlign: 'left',
                      mb: 4,
                      height: isMobile ? '500px' : '81vh',
                      overflowY: 'auto',
                    }}
                  >
                    {blueprintData.structures.map((structure) => (
                      <Accordion
                        elevation={0}
                        key={structure.id}
                        sx={{ boxShadow: 'none', border: 'none' }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            backgroundColor: 'background.paper',
                            border: 'none',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              pr: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 'medium' }}
                            >
                              {structure.name_fr ||
                                structure.name_en ||
                                structure.name}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 'bold',
                                color:
                                  blueprintData?.mainColor || 'primary.main',
                              }}
                            >
                              {structure.tabs
                                .reduce(
                                  (acc, tab) =>
                                    acc +
                                    (tab.blocks || []).reduce(
                                      (tabAcc, block) =>
                                        tabAcc +
                                        (block.price ? Number(block.price) : 0),
                                      0
                                    ),
                                  0
                                )
                                .toFixed(2)}{' '}
                              $
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails elevation={0}>
                          {(structure.tabs || []).map((tab, tabIndex) => (
                            <Box key={tabIndex} sx={{ mb: 2 }}>
                              {(tab.blocks || []).map((block) => (
                                <Box
                                  key={block.i}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                    pl: 2,
                                  }}
                                >
                                  <Typography>
                                    {block.name_fr ||
                                      block.name ||
                                      `Block ${block.i}`}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 'bold',
                                      color:
                                        blueprintData?.mainColor ||
                                        'primary.main',
                                    }}
                                  >
                                    {block.price
                                      ? `${Number(block.price).toFixed(2)} $`
                                      : '0.00 $'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ))}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mt: 2,
                              pt: 2,
                              borderTop: 1,
                              borderColor: 'divider',
                              backgroundColor: 'background.paper',
                              p: 2,
                              borderRadius: '4px',
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {t('blueprint.total_structure')}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 'bold',
                                color:
                                  blueprintData?.mainColor || 'primary.main',
                              }}
                            >
                              {structure.tabs
                                .reduce(
                                  (acc, tab) =>
                                    acc +
                                    (tab.blocks || []).reduce(
                                      (tabAcc, block) =>
                                        tabAcc +
                                        (block.price ? Number(block.price) : 0),
                                      0
                                    ),
                                  0
                                )
                                .toFixed(2)}{' '}
                              $
                            </Typography>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 2,
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold' }}
                      >
                        {t('blueprint.total_structures')}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold' }}
                      >
                        {blockPrices.toFixed(2)} $
                      </Typography>
                    </Box>

                    <Box>
                      {blueprintData?.modules
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((module) => (
                          <Accordion
                            key={module.id}
                            elevation={0}
                            sx={{ boxShadow: 'none', border: 'none', mb: 1 }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                backgroundColor: 'background.paper',
                                border: 'none',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  pr: 2,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 'medium' }}
                                >
                                  {module.name_fr ||
                                    module.name_en ||
                                    module.name}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 'bold',
                                    color:
                                      blueprintData?.mainColor ||
                                      'primary.main',
                                  }}
                                >
                                  {module.price
                                    ? `${Number(module.price).toFixed(2)} $`
                                    : '0.00 $'}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails elevation={0}>
                              <Box sx={{ pl: 2 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {module.description_fr ||
                                    module.description_en ||
                                    module.description ||
                                    t('blueprint.no_description')}
                                </Typography>
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mt: 2,
                          pt: 2,
                          borderTop: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {t('blueprint.total_modules')}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {modulePrices.toFixed(2)} $
                        </Typography>
                      </Box>
                    </Box>
                    {/* Total Section */}
                    <Box
                      sx={{
                        mt: 4,
                        pt: 2,
                        borderTop: 2,
                        borderColor: blueprintData?.mainColor || 'primary.main',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {t('blueprint.total_general')}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            color: blueprintData?.mainColor || 'primary.main',
                          }}
                        >
                          {totalPrice.toFixed(2)} $
                        </Typography>
                      </Box>
                    </Box>

                    {/* Footer */}
                    <Box
                      sx={{
                        mt: 4,
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      <Typography variant="body2">
                        {t('blueprint.generated_by_blueprint_editor')}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={openContractDialog}
        onClose={handleCloseContract}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            '& .MuiDialogContent-root': {
              backgroundColor: 'white',
              padding: '30px',
            },
            '& .MuiDialog-paper': {
              backgroundColor: 'white',
            },
            '& .MuiDialog-container': {
              backgroundColor: 'white',
            },
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: 'white' }}>
          {t('contract.title')}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'white' }}>
          <Box sx={{ mt: 2, backgroundColor: 'white' }}>
            <Typography variant="h6" gutterBottom>
              {t('contract.project_info')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('contract.project_name')}: {blueprintData?.name}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('contract.total_price')}: {totalPrice.toFixed(2)} $
            </Typography>
            <Typography variant="body1" paragraph>
              {t('contract.id')}: {blueprintData?.id || t('contract.no_id')}
            </Typography>

            <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
              {t('contract.client_info')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  {t('contract.name')}:{' '}
                  {blueprintData?.name || t('contract.not_specified')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">
                  {t('contract.email')}:{' '}
                  {blueprintData?.email || t('contract.not_specified')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {t('contract.address')}:{' '}
                  {blueprintData?.address || t('contract.not_specified')}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('contract.terms')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                1. {t('contract.object')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.object_description')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                2. {t('contract.services')}
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <Box
                  component="ul"
                  sx={{
                    margin: 0,
                    paddingLeft: '20px',
                    '& li': {
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      marginBottom: '8px',
                    },
                  }}
                >
                  <Typography component="li" variant="body2">
                    {t('contract.service1')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.service2')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.service3')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.service4')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.service5')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.service6')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('contract.services_note')}
                </Typography>
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                3. {t('contract.hours_bank')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.hours_bank_description', {
                  amount: '6 270',
                  hours: '30',
                  rate: '209',
                })}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.hours_bank_renewal')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.hours_bank_validity')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                4. {t('contract.hours_tracking')}
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <Box
                  component="ul"
                  sx={{
                    margin: 0,
                    paddingLeft: '20px',
                    '& li': {
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      marginBottom: '8px',
                    },
                  }}
                >
                  <Typography component="li" variant="body2">
                    {t('contract.tracking1')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.tracking2')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.tracking3')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('contract.tracking_note')}
                </Typography>
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                5. {t('contract.payment_terms')}
              </Typography>
              <Typography variant="body2" component="div" paragraph>
                <Box
                  component="ul"
                  sx={{
                    margin: 0,
                    paddingLeft: '20px',
                    '& li': {
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                      marginBottom: '8px',
                    },
                  }}
                >
                  <Typography component="li" variant="body2">
                    {t('contract.payment1')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.payment2')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.payment3')}
                  </Typography>
                  <Typography component="li" variant="body2">
                    {t('contract.payment4')}
                  </Typography>
                </Box>
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                6. {t('contract.timelines')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.timelines_description')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                7. {t('contract.intellectual_property')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.intellectual_property_description')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                8. {t('contract.confidentiality')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.confidentiality_description')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                9. {t('contract.termination')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.termination_description')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                10. {t('contract.applicable_law')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.applicable_law_description')}
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mt: 2 }}
              >
                11. {t('contract.appendix')}
              </Typography>
              <Typography variant="body2" paragraph>
                {t('contract.appendix_description')}
              </Typography>
            </Box>

            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contractAccepted}
                    onChange={(e) => setContractAccepted(e.target.checked)}
                  />
                }
                label={t('contract.accept_terms')}
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('contract.signature')}
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  p: 1,
                  backgroundColor: '#fff',
                  width: '350px',
                }}
              >
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    width: 350,
                    height: 120,
                    className: 'signature-canvas',
                  }}
                  onEnd={handleSignatureEnd}
                />
              </Box>
              <Button
                onClick={handleClearSignature}
                variant="outlined"
                sx={{ mt: 1 }}
              >
                {t('contract.clear_signature')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContract}>{t('contract.cancel')}</Button>
          <Button
            onClick={handleContractSubmit}
            variant="contained"
            sx={{
              backgroundColor: blueprintData?.mainColor || 'primary.main',
              '&:hover': {
                backgroundColor: blueprintData?.mainColor || 'primary.main',
                opacity: 0.9,
              },
            }}
            disabled={!contractAccepted || isSignatureEmpty}
          >
            {t('contract.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPaymentForm}
        onClose={handleClosePayment}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            '& .MuiDialogContent-root': {
              backgroundColor: 'white',
              padding: '20px',
            },
            '& .MuiDialog-paper': {
              backgroundColor: 'white',
            },
            '& .MuiDialog-container': {
              backgroundColor: 'white',
            },
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: 'white' }}>
          {t('payment.title')}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'white' }}>
          <Box sx={{ mt: 2, backgroundColor: 'white' }}>
            {clientSecret && (
              <Elements
                stripe={stripePromiseInstance}
                options={{ clientSecret }}
              >
                <PaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  blueprintData={blueprintData}
                  t={t}
                  blueprintId={blueprintId}
                />
              </Elements>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {paymentError && (
        <Snackbar
          open={!!paymentError}
          autoHideDuration={6000}
          onClose={() => setPaymentError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="error"
            onClose={() => setPaymentError(null)}
            sx={{ width: '100%' }}
          >
            {paymentError}
          </Alert>
        </Snackbar>
      )}

      {paymentSuccess && (
        <Snackbar
          open={paymentSuccess}
          autoHideDuration={6000}
          onClose={() => setPaymentSuccess(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="success"
            onClose={() => setPaymentSuccess(false)}
            sx={{ width: '100%' }}
          >
            {t('payment.success')}
          </Alert>
        </Snackbar>
      )}
    </Container>
  );
};

export default PublicBlueprintPage;
